import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  FolderOpen, Upload, Download, CheckCircle, Search, 
  RefreshCw, Shield, FileText, Check
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const DOC_TYPES = [
  { value: 'contract', label: 'Employment Contract' },
  { value: 'sin', label: 'SIN Document' },
  { value: 'banking', label: 'Banking / Direct Deposit' },
  { value: 'tax', label: 'Tax Forms' },
  { value: 'other', label: 'Other' }
];

export default function Onboarding() {
  const { api, isAdmin, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadData, setUploadData] = useState({
    doc_type: 'contract',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedEmployee]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (isAdmin && selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      const docsRes = await api.get('/onboarding', { params });
      setDocuments(docsRes.data);

      if (isAdmin) {
        const usersRes = await api.get('/users');
        setEmployees(usersRes.data.filter(u => u.role === 'employee'));

        if (selectedEmployee) {
          const checklistRes = await api.get(`/onboarding/checklist/${selectedEmployee}`);
          setChecklist(checklistRes.data);
        }
      }
    } catch (error) {
      console.error('Onboarding fetch error:', error);
      let errorMsg = 'Failed to load onboarding documents';
      
      if (error.response?.status === 401) {
        errorMsg = 'Session expired. Please log in again.';
      } else if (error.response?.status === 403) {
        if (error.response?.data?.detail?.includes('MFA')) {
          errorMsg = 'MFA verification required. Please re-authenticate.';
        } else if (error.response?.data?.detail?.includes('physician')) {
          errorMsg = 'Onboarding documents are only available for employees.';
        } else {
          errorMsg = error.response?.data?.detail || 'Access denied';
        }
      } else if (error.response?.status === 404) {
        errorMsg = 'Onboarding service not available';
      } else if (error.message === 'Network Error') {
        errorMsg = 'Unable to reach server. Check your connection.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('doc_type', uploadData.doc_type);
    formData.append('description', uploadData.description || '');

    try {
      await api.post('/onboarding/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ doc_type: 'contract', description: '' });
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/onboarding/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedEmployee) return;
    try {
      await api.post(`/onboarding/checklist/${selectedEmployee}/complete`);
      toast.success('Onboarding marked as complete');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark onboarding complete');
    }
  };

  const getDocTypeLabel = (type) => {
    return DOC_TYPES.find(d => d.value === type)?.label || type;
  };

  const getDocTypeBadgeColor = (type) => {
    const colors = {
      contract: 'bg-blue-100 text-blue-700',
      sin: 'bg-purple-100 text-purple-700',
      banking: 'bg-emerald-100 text-emerald-700',
      tax: 'bg-amber-100 text-amber-700',
      other: 'bg-slate-100 text-slate-700'
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Employee Onboarding</h1>
          <p className="text-slate-500 mt-1">
            {isAdmin ? 'Review employee onboarding documents' : 'Upload your onboarding documents'}
          </p>
        </div>
        {!isAdmin && (
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-onboarding-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Security Notice */}
      <Alert className="bg-teal-50 border-teal-200">
        <Shield className="h-4 w-4 text-teal-600" />
        <AlertDescription className="text-teal-700">
          <strong>Secure Upload:</strong> Your onboarding documents are stored securely and only visible to you and administrators.
        </AlertDescription>
      </Alert>

      {/* Admin: Employee Selector and Checklist */}
      {isAdmin && (
        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Select Employee</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedEmployee || ''} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="employee-selector">
                  <SelectValue placeholder="Choose employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {selectedEmployee && checklist && (
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Onboarding Checklist</CardTitle>
                  {checklist.completed ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Complete
                    </Badge>
                  ) : (
                    <Button 
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleMarkComplete}
                      data-testid="mark-complete-btn"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Mark Complete
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {DOC_TYPES.slice(0, 4).map(docType => {
                    const isUploaded = checklist[`${docType.value}_uploaded`];
                    return (
                      <div 
                        key={docType.value}
                        className={`flex items-center gap-3 p-3 rounded-lg ${
                          isUploaded ? 'bg-emerald-50' : 'bg-slate-50'
                        }`}
                        data-testid={`checklist-${docType.value}`}
                      >
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          isUploaded ? 'bg-emerald-100' : 'bg-slate-200'
                        }`}>
                          {isUploaded ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <FileText className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isUploaded ? 'text-emerald-700' : 'text-slate-600'}`}>
                            {docType.label}
                          </p>
                          <p className="text-xs text-slate-500">
                            {isUploaded ? 'Uploaded' : 'Pending'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Employee: Document Types Guide */}
      {!isAdmin && (
        <Card className="bg-slate-50">
          <CardContent className="py-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Required Documents:</p>
            <div className="flex flex-wrap gap-2">
              {DOC_TYPES.map(docType => {
                const hasDoc = documents.some(d => d.doc_type === docType.value);
                return (
                  <Badge 
                    key={docType.value}
                    className={hasDoc ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}
                  >
                    {hasDoc && <CheckCircle className="h-3 w-3 mr-1" />}
                    {docType.label}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isAdmin && selectedEmployee 
              ? `Documents for ${employees.find(e => e.id === selectedEmployee)?.first_name || 'Employee'}`
              : 'Your Documents'
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-slate-300" />
              <p className="mt-4 text-slate-500">
                {isAdmin ? 'Select an employee to view their documents' : 'No documents uploaded yet'}
              </p>
              {!isAdmin && (
                <Button 
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Your First Document
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`onboarding-doc-${doc.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{doc.filename}</p>
                          {doc.description && (
                            <p className="text-sm text-slate-500">{doc.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getDocTypeBadgeColor(doc.doc_type)}>
                        {getDocTypeLabel(doc.doc_type)}
                      </Badge>
                    </TableCell>
                    <TableCell>{format(parseISO(doc.uploaded_at), 'MMM d, yyyy')}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-btn-${doc.id}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upload Onboarding Document</DialogTitle>
            <DialogDescription>Upload a required onboarding document</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select 
                  value={uploadData.doc_type} 
                  onValueChange={(value) => setUploadData({ ...uploadData, doc_type: value })}
                >
                  <SelectTrigger data-testid="upload-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(docType => (
                      <SelectItem key={docType.value} value={docType.value}>
                        {docType.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  placeholder="Add any notes about this document..."
                  data-testid="upload-description"
                />
              </div>
              <div className="space-y-2">
                <Label>File</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  data-testid="upload-file"
                />
                <p className="text-xs text-slate-500">
                  Accepted formats: PDF, DOC, DOCX, JPG, PNG
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={uploadLoading}
                data-testid="upload-submit"
              >
                {uploadLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
